import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BRAND } from '../../../shared/config/brand';
import { RefreshCwIcon, SearchIcon, FilterIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, TrendingUpIcon, TrendingDownIcon, DollarSignIcon, FileTextIcon } from 'lucide-react';
import { Refund, RefundStatus, RefundReason } from '../../../shared/types/Refund';
export function Refunds() {
  const {
    t
  } = useTranslation('refunds');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<RefundStatus | 'all'>('all');
  const [reasonFilter, setReasonFilter] = useState<RefundReason | 'all'>('all');
  // Mock data
  const refunds: Refund[] = [{
    id: 'RF-2024-0045',
    refundId: 'RF-2024-0045',
    transactionId: 'TXN-2026-5512',
    originalAmount: 45000,
    refundAmount: 45000,
    currency: 'MYR',
    status: 'pending_approval',
    reason: 'customer_request',
    reasonDescription: 'Customer requested refund — order delivered late',
    requestedBy: 'Ahmad Rahman',
    requestedAt: '2026-05-04T09:00:00Z',
    customerName: 'Ahmad Rahman',
    customerEmail: 'ahmad.rahman@email.com',
    paymentMethod: 'Visa',
    cardLast4: '4521',
    merchantName: BRAND.name,
    merchantId: 'MERCH-MY-001',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'pending'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2026-05-04T09:00:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'Ahmad Rahman'
    }, {
      id: '2',
      timestamp: '2026-05-04T09:05:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }],
    isPartial: false
  }, {
    id: '1',
    refundId: 'REF-2024-001',
    transactionId: 'TXN-2024-1234',
    originalAmount: 125000,
    refundAmount: 125000,
    currency: 'MYR',
    status: 'pending_approval',
    reason: 'customer_request',
    reasonDescription: 'Customer requested cancellation due to order error',
    requestedBy: 'John Smith',
    requestedAt: '2024-01-18T10:30:00Z',
    customerName: 'John Smith',
    customerEmail: 'john.smith@email.com',
    paymentMethod: 'Visa',
    cardLast4: '4242',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'pending'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-18T10:30:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'John Smith'
    }, {
      id: '2',
      timestamp: '2024-01-18T10:35:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }],
    isPartial: false
  }, {
    id: '2',
    refundId: 'REF-2024-002',
    transactionId: 'TXN-2024-1567',
    originalAmount: 87500,
    refundAmount: 43750,
    currency: 'MYR',
    status: 'processing',
    reason: 'product_defective',
    reasonDescription: 'Product arrived damaged, partial refund issued',
    requestedBy: 'Sarah Johnson',
    requestedAt: '2024-01-15T14:20:00Z',
    approvedBy: 'Mike Manager',
    approvedAt: '2024-01-15T15:00:00Z',
    customerName: 'Sarah Johnson',
    customerEmail: 'sarah.j@email.com',
    paymentMethod: 'Mastercard',
    cardLast4: '5555',
    merchantName: 'XYZ Shop',
    merchantId: 'MERCH-002',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Mike Manager',
      approverEmail: 'mike.m@merchant.com',
      status: 'approved',
      timestamp: '2024-01-15T15:00:00Z',
      notes: 'Approved partial refund for damaged item'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T14:20:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'Sarah Johnson'
    }, {
      id: '2',
      timestamp: '2024-01-15T14:25:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }, {
      id: '3',
      timestamp: '2024-01-15T15:00:00Z',
      status: 'approved',
      description: 'Refund approved by Mike Manager',
      actor: 'Mike Manager'
    }, {
      id: '4',
      timestamp: '2024-01-15T15:05:00Z',
      status: 'processing',
      description: 'Processing refund to original payment method'
    }],
    isPartial: true
  }, {
    id: '3',
    refundId: 'REF-2024-003',
    transactionId: 'TXN-2024-0892',
    originalAmount: 45000,
    refundAmount: 45000,
    currency: 'MYR',
    status: 'completed',
    reason: 'duplicate_charge',
    reasonDescription: 'Duplicate transaction, full refund issued',
    requestedBy: 'Michael Brown',
    requestedAt: '2024-01-10T09:00:00Z',
    approvedBy: 'Admin User',
    approvedAt: '2024-01-10T09:15:00Z',
    processedAt: '2024-01-10T09:20:00Z',
    completedAt: '2024-01-12T10:30:00Z',
    customerName: 'Michael Brown',
    customerEmail: 'mbrown@email.com',
    paymentMethod: 'Visa',
    cardLast4: '1234',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Admin User',
      approverEmail: 'admin@merchant.com',
      status: 'approved',
      timestamp: '2024-01-10T09:15:00Z',
      notes: 'Duplicate charge confirmed, approved immediately'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-10T09:00:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'Michael Brown'
    }, {
      id: '2',
      timestamp: '2024-01-10T09:15:00Z',
      status: 'approved',
      description: 'Refund approved by Admin User',
      actor: 'Admin User'
    }, {
      id: '3',
      timestamp: '2024-01-10T09:20:00Z',
      status: 'processing',
      description: 'Processing refund to original payment method'
    }, {
      id: '4',
      timestamp: '2024-01-12T10:30:00Z',
      status: 'completed',
      description: 'Refund successfully completed'
    }],
    isPartial: false
  }, {
    id: '4',
    refundId: 'REF-2024-004',
    transactionId: 'TXN-2024-0654',
    originalAmount: 165000,
    refundAmount: 165000,
    currency: 'MYR',
    status: 'rejected',
    reason: 'customer_request',
    reasonDescription: 'Customer requested refund after 30-day window',
    requestedBy: 'Emily Davis',
    requestedAt: '2024-01-08T11:20:00Z',
    rejectedBy: 'Sarah Manager',
    rejectedAt: '2024-01-08T14:00:00Z',
    rejectionReason: 'Refund request exceeds the 30-day return policy window',
    customerName: 'Emily Davis',
    customerEmail: 'emily.d@email.com',
    paymentMethod: 'Mastercard',
    cardLast4: '8888',
    merchantName: 'XYZ Shop',
    merchantId: 'MERCH-002',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'rejected',
      timestamp: '2024-01-08T14:00:00Z',
      notes: 'Request outside return policy window'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-08T11:20:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'Emily Davis'
    }, {
      id: '2',
      timestamp: '2024-01-08T11:25:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }, {
      id: '3',
      timestamp: '2024-01-08T14:00:00Z',
      status: 'rejected',
      description: 'Refund rejected by Sarah Manager',
      actor: 'Sarah Manager'
    }],
    isPartial: false
  }, {
    id: '5',
    refundId: 'REF-2024-005',
    transactionId: 'TXN-2024-2341',
    originalAmount: 95000,
    refundAmount: 95000,
    currency: 'MYR',
    status: 'requested',
    reason: 'product_not_received',
    reasonDescription: 'Customer claims product was never delivered',
    requestedBy: 'David Wilson',
    requestedAt: '2024-01-19T15:45:00Z',
    customerName: 'David Wilson',
    customerEmail: 'dwilson@email.com',
    paymentMethod: 'Visa',
    cardLast4: '6789',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'pending'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-19T15:45:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'David Wilson'
    }],
    isPartial: false
  }, {
    id: '6',
    refundId: 'REF-2024-006',
    transactionId: 'TXN-2024-1823',
    originalAmount: 215000,
    refundAmount: 215000,
    currency: 'MYR',
    status: 'failed',
    reason: 'billing_error',
    reasonDescription: 'Billing error detected, refund attempted',
    requestedBy: 'System',
    requestedAt: '2024-01-17T08:00:00Z',
    approvedBy: 'Admin User',
    approvedAt: '2024-01-17T08:05:00Z',
    processedAt: '2024-01-17T08:10:00Z',
    customerName: 'Jennifer Lee',
    customerEmail: 'jlee@email.com',
    paymentMethod: 'Visa',
    cardLast4: '9876',
    merchantName: 'XYZ Shop',
    merchantId: 'MERCH-002',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Admin User',
      approverEmail: 'admin@merchant.com',
      status: 'approved',
      timestamp: '2024-01-17T08:05:00Z',
      notes: 'System detected billing error, auto-approved'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-17T08:00:00Z',
      status: 'requested',
      description: 'Refund requested by system',
      actor: 'System'
    }, {
      id: '2',
      timestamp: '2024-01-17T08:05:00Z',
      status: 'approved',
      description: 'Refund auto-approved',
      actor: 'Admin User'
    }, {
      id: '3',
      timestamp: '2024-01-17T08:10:00Z',
      status: 'processing',
      description: 'Processing refund to original payment method'
    }, {
      id: '4',
      timestamp: '2024-01-17T08:15:00Z',
      status: 'failed',
      description: 'Refund failed - card expired'
    }],
    isPartial: false
  }];
  // Calculate statistics
  const totalRefunds = refunds.length;
  const pendingRefunds = refunds.filter(r => ['requested', 'pending_approval', 'approved', 'processing'].includes(r.status)).length;
  const completedRefunds = refunds.filter(r => r.status === 'completed').length;
  const totalRefundAmount = refunds.filter(r => r.status === 'completed').reduce((sum, r) => sum + r.refundAmount, 0);
  // Filter refunds
  const filteredRefunds = refunds.filter(refund => {
    const matchesSearch = refund.refundId.toLowerCase().includes(searchQuery.toLowerCase()) || refund.transactionId.toLowerCase().includes(searchQuery.toLowerCase()) || refund.customerName.toLowerCase().includes(searchQuery.toLowerCase()) || refund.customerEmail.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || refund.status === statusFilter;
    const matchesReason = reasonFilter === 'all' || refund.reason === reasonFilter;
    return matchesSearch && matchesStatus && matchesReason;
  });
  const getStatusBadge = (status: RefundStatus) => {
    const styles = {
      requested: 'bg-blue-100 text-blue-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      requested: 'Requested',
      pending_approval: 'Pending Approval',
      approved: 'Approved',
      processing: 'Processing',
      completed: 'Completed',
      rejected: 'Rejected',
      failed: 'Failed',
      cancelled: 'Cancelled'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-9690" data-mp="3q4033c4fgff0">
        {labels[status]}
      </span>;
  };
  const getReasonLabel = (reason: RefundReason) => {
    const labels = {
      customer_request: 'Customer Request',
      duplicate_charge: 'Duplicate Charge',
      fraudulent: 'Fraudulent',
      product_not_received: 'Product Not Received',
      product_defective: 'Product Defective',
      service_unsatisfactory: 'Service Unsatisfactory',
      billing_error: 'Billing Error',
      other: 'Other'
    };
    return labels[reason];
  };
  return <div className="space-y-6" data-id="element-9691" data-mp="15e16fysu1srp">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-9692" data-mp="gog26m5812h2">
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-9693" data-mp="bp3iubdk6dj8">
          <div className="flex items-center justify-between" data-id="element-9694" data-mp="bn0hiv6ygxz7">
            <div data-id="element-9695" data-mp="6opa0r44uzk5">
              <div className="text-sm font-medium text-gray-500" data-id="element-9696" data-mp="6o5qx72qy54j">
                {t('pages.Refunds.total_refunds')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-9697" data-mp="3tuq1z1q20tse">
                {totalRefunds}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-9698" data-mp="3p37yo70k6h49">
              <RefreshCwIcon size={20} className="text-blue-600" data-id="element-9699" data-mp="3k5ok15zy82bs" />
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-9700" data-mp="3f7dcx311g5x9">
          <div className="flex items-center justify-between" data-id="element-9701" data-mp="3a5fjxh2u5wr4">
            <div data-id="element-9702" data-mp="365duc6adkts7">
              <div className="text-sm font-medium text-gray-500" data-id="element-9703" data-mp="364vp4k5qj585">
                {t('pages.Refunds.pending_refunds')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-9704" data-mp="1lmtkenw0gsjr">
                {pendingRefunds}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center" data-id="element-9705" data-mp="1uo1708p604y0">
              <ClockIcon size={20} className="text-yellow-600" data-id="element-9706" data-mp="1zne21gq6wibo" />
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-9707" data-mp="11zvjje6q81uh">
          <div className="flex items-center justify-between" data-id="element-9708" data-mp="16xxbkom4rbi3">
            <div data-id="element-9709" data-mp="16w44j87lup9k">
              <div className="text-sm font-medium text-gray-500" data-id="element-9710" data-mp="1bx9zlcoqmom9">
                {t('pages.Refunds.completed_refunds')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-9711" data-mp="1gp1qxpfpwydg">
                {completedRefunds}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-9712" data-mp="j6rygh7kqxbh">
              <CheckCircleIcon size={20} className="text-green-600" data-id="element-9713" data-mp="o5cnrllafwl5" />
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-9714" data-mp="c5ebeblkvevt">
          <div className="flex items-center justify-between" data-id="element-9715" data-mp="c3levp4oib1j">
            <div data-id="element-9716" data-mp="pvhu4sl90qgi">
              <div className="text-sm font-medium text-gray-500" data-id="element-9717" data-mp="pw18jzbowwps">
                {t('pages.Refunds.total_refunded')}
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-9718" data-mp="3td9p8fuolqix">
                {t('pages.RefundDetail.usd')}{' '}
                {totalRefundAmount.toLocaleString('en-US')}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-9719" data-mp="76l636gg2u6q">
              <DollarSignIcon size={20} className="text-red-600" data-id="element-9720" data-mp="26yt8y4j3fyg" />
            </div>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200" data-id="element-9721" data-mp="1inl0c7mcrj3e">
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-4" data-id="element-9722" data-mp="1dlvlshzx8nak">
          <div className="relative flex-grow" data-id="element-9723" data-mp="2vigvluz9zigm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9724" data-mp="30iv57hgiz0gr">
              <SearchIcon size={16} className="text-gray-400" data-id="element-9725" data-mp="2mjlnuegl20b7" />
            </div>
            <input type="search" aria-label={t('pages.Refunds.attrs.search_by_refund_id_transaction_id')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={t('pages.Refunds.attrs.search_by_refund_id_transaction_id')} data-id="element-9726" data-mp="2mloqbxyx9lwt" />
          </div>
          <div className="flex gap-2" data-id="element-9727" data-mp="20g4d00am9qy">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as RefundStatus | 'all')} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-9728" data-mp="1zegfuy6hsrt">
              <option value="all" data-id="element-9729" data-mp="1t4zs268xzo7s">{t('pages.Refunds.all_statuses')}</option>
              <option value="requested" data-id="element-9730" data-mp="1o6g5q4qfsaew">{t('pages.Refunds.requested')}</option>
              <option value="pending_approval" data-id="element-9731" data-mp="1o4d5krvkl0wg">
                {t('pages.Refunds.pending_approval')}
              </option>
              <option value="approved" data-id="element-9732" data-mp="2gv3u496hffwo">{t('pages.Refunds.approved')}</option>
              <option value="processing" data-id="element-9733" data-mp="2gt0uqyjfwafa">
                {t('pages.Refunds.processing')}
              </option>
              <option value="completed" data-id="element-9734" data-mp="2bxv980ty8xrq">{t('pages.Refunds.completed')}</option>
              <option value="rejected" data-id="element-9735" data-mp="27u5mzi0qys9v">{t('pages.Refunds.rejected')}</option>
              <option value="failed" data-id="element-9736" data-mp="10hn53uyvdedy">{t('pages.Refunds.failed')}</option>
              <option value="cancelled" data-id="element-9737" data-mp="wf0luig9l1sd">{t('pages.Refunds.cancelled')}</option>
            </select>
            <select value={reasonFilter} onChange={e => setReasonFilter(e.target.value as RefundReason | 'all')} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-9738" data-mp="383eveth2p9tv">
              <option value="all" data-id="element-9739" data-mp="3lvbj8lbpkxkg">{t('pages.Refunds.all_reasons')}</option>
              <option value="customer_request" data-id="element-9740" data-mp="3qs221gewe79k">
                {t('pages.Refunds.customer_request')}
              </option>
              <option value="duplicate_charge" data-id="element-9741" data-mp="3vqdabmpjo9po">
                {t('pages.Refunds.duplicate_charge')}
              </option>
              <option value="fraudulent" data-id="element-9742" data-mp="8ni90vrwhpf9">
                {t('pages.Refunds.fraudulent')}
              </option>
              <option value="product_not_received" data-id="element-9743" data-mp="25g0kzq26ssdh">
                {t('pages.Refunds.product_not_received')}
              </option>
              <option value="product_defective" data-id="element-9744" data-mp="2afwrbvycwvw7">
                {t('pages.Refunds.product_defective')}
              </option>
              <option value="service_unsatisfactory" data-id="element-9745" data-mp="1u9y8knxp3na9">
                {t('pages.Refunds.service_unsatisfactory')}
              </option>
              <option value="billing_error" data-id="element-9746" data-mp="246krh76go49n">
                {t('pages.Refunds.billing_error')}
              </option>
              <option value="other" data-id="element-9747" data-mp="28x9i6kabgx7q">{t('pages.Refunds.other')}</option>
            </select>
          </div>
        </div>
        {/* Refunds List */}
        <div className="overflow-x-auto" data-id="element-9748" data-mp="2d0pfliu7n1lh">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-9749" data-mp="2hx6biz7nik94">
            <thead className="bg-gray-50" data-id="element-9750" data-mp="2hzivfhwyz4ot">
              <tr data-id="element-9751" data-mp="2mxu3bn3pf54e">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9752" data-mp="2mxbyi22z7ikv">
                  {t('components.RefundModal.refund')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9753" data-mp="sk2x6xkl33dp">
                  {t('pages.Refunds.customer')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9754" data-mp="3asdx8k5wt9mk">
                  {t('pages.Refunds.amount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9755" data-mp="31zkf8ez2vw50">
                  {t('components.RefundModal.reason')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9756" data-mp="3tqzzi2d5x6s2">
                  {t('pages.Refunds.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9757" data-mp="3pnae1lrsb5b9">
                  {t('pages.Refunds.requested')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9758" data-mp="3fqp6asodrg3q">
                  {t('pages.Refunds.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-9759" data-mp="24e4w3n2nql57">
              {filteredRefunds.map(refund => <tr key={refund.id} onClick={() => navigate(`/refund-detail/${refund.id}`)} className="hover:bg-gray-50 cursor-pointer" data-id="element-9760" data-mp="2asbtj6gd0i8f">
                  <td className="px-6 py-4" data-id="element-9761" data-mp="21rdn6hig7ymo">
                    <div data-id="element-9762" data-mp="21qb1qp3waoe5">
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-9763" data-mp="21tqehla3jlce">
                        {refund.refundId}
                        {refund.isPartial && <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-medium rounded" data-id="element-9764" data-mp="1rur7e74zbxri">
                            {t('pages.Refunds.partial')}
                          </span>}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-9765" data-mp="143lsbu6v59i9">
                        {refund.transactionId}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-9766" data-mp="pgmyxtog450w">
                    <div data-id="element-9767" data-mp="phewvb5imeuc">
                      <div className="text-sm font-medium text-gray-900" data-id="element-9768" data-mp="pizoot0g3joi">
                        {refund.customerName}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-9769" data-mp="zgdt1u93gi0g">
                        {refund.customerEmail}
                      </div>
                      <div className="text-xs text-gray-400 mt-1" data-id="element-9770" data-mp="uicffouftawd">
                        {refund.paymentMethod}{' '}
                        {refund.cardLast4 && `•••• ${refund.cardLast4}`}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-9771" data-mp="1i4iv8xa3rux5">
                    <div data-id="element-9772" data-mp="1d6r3c39gk8sy">
                      <div className="text-sm font-medium text-gray-900" data-id="element-9773" data-mp="1d67ncs3dbuhk">
                        {t('pages.RefundDetail.usd')}{' '}
                        {refund.refundAmount.toLocaleString('en-US')}
                      </div>
                      {refund.isPartial && <div className="text-xs text-gray-500" data-id="element-9774" data-mp="3gnnn79mk5ny1">
                          {t('pages.RefundDetail.of_usd')}{' '}
                          {refund.originalAmount.toLocaleString('en-US')}
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-9775" data-mp="3vcadu7hmy24k">
                    <div className="text-sm text-gray-900" data-id="element-9776" data-mp="3vb8s992ztx8l">
                      {getReasonLabel(refund.reason)}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-9777" data-mp="32ncvk4dfc5bt">{getStatusBadge(refund.status)}</td>
                  <td className="px-6 py-4" data-id="element-9778" data-mp="37mz96ex5znl5">
                    <div className="text-sm text-gray-900" data-id="element-9779" data-mp="37lwm6i2yq5ai">
                      {new Date(refund.requestedAt).toLocaleDateString('en-GB')}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-9780" data-mp="mx0dp4xb36zf">
                      {t('pages.Refunds.by')} {refund.requestedBy}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right text-sm" data-id="element-9781" data-mp="r06ie84rsq3s">
                    <button onClick={e => {
                  e.stopPropagation();
                  navigate(`/refund-detail/${refund.id}`);
                }} className="text-blue-600 hover:text-blue-900 font-medium" data-id="element-9782" data-mp="2bf314y15wh0o">
                      {t('pages.Refunds.view_details')}
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {filteredRefunds.length === 0 && <div className="py-12 flex flex-col items-center justify-center text-center" data-id="element-9783" data-mp="1nt4rkyn46424">
            <RefreshCwIcon size={48} className="text-gray-400 mb-4" data-id="element-9784" data-mp="1jrazheqzi8dk" />
            <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-9785" data-mp="1erojixjrue12">
              {t('pages.Refunds.no_refunds_found')}
            </h3>
            <p className="text-gray-500 max-w-md" data-id="element-9786" data-mp="3e0vaiyzbhbxo">
              {t('pages.Refunds.no_refunds_match_your_current_search')}
            </p>
          </div>}
      </div>
    </div>;
}